import React, { useMemo } from 'react';
import { Tag } from 'antd';
import { LineChartOutlined } from '@ant-design/icons';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine,
    ResponsiveContainer,
    LabelList,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const rank = payload[0]?.value;
    const isToday = payload[0]?.payload?.isToday;
    return (
        <div
            style={{
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 14px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            }}
        >
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
                {label}
                {isToday ? ' (오늘)' : ''}
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: rank ? '#0f172a' : '#94a3b8' }}>
                {rank ? `${rank}위` : '순위밖'}
            </div>
        </div>
    );
};

const CustomDot = props => {
    const { cx, cy, payload } = props;
    if (!payload?.rank) return null;
    if (payload.isToday) {
        return <circle cx={cx} cy={cy} r={6} fill="#4f46e5" stroke="white" strokeWidth={2.5} />;
    }
    return <circle cx={cx} cy={cy} r={3} fill="#4f46e5" stroke="white" strokeWidth={1.5} />;
};

const StatChip = ({ label, value, color }) => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '3px 10px',
            background: '#f8fafc',
            borderRadius: 20,
            border: '1px solid #e2e8f0',
        }}
    >
        <span style={{ fontSize: 11, color: '#94a3b8' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: color || '#0f172a' }}>{value}</span>
    </div>
);

const SAMPLE_DATA = [
    { date: '03-21', rank: 42 },
    { date: '03-24', rank: 28 },
    { date: '03-27', rank: 22 },
    { date: '03-31', rank: 18 },
    { date: '04-04', rank: 25 },
    { date: '04-08', rank: 12 },
    { date: '04-12', rank: 15 },
    { date: '04-16', rank: 18 },
    { date: '04-20', rank: 20 },
];

const RankTrendChart = ({ report }) => {
    const rawData = report?.rankHistory;
    const hasData = rawData && rawData.length > 0 && rawData.some(d => d.rank > 0);

    const mapped = hasData
        ? rawData.map(d => ({
              date: d.date.slice(5),
              rank: d.rank || null,
              isToday: d.isToday || false,
          }))
        : SAMPLE_DATA;

    // 앞뒤 null 제거, 중간 null은 connectNulls로 연결
    const firstValid = mapped.findIndex(d => d.rank !== null);
    const lastValid = [...mapped].reverse().findIndex(d => d.rank !== null);
    const data = firstValid === -1 ? mapped : mapped.slice(firstValid, mapped.length - lastValid);

    const valid = data.filter(d => d.rank > 0);
    const ranks = valid.map(d => d.rank);
    const minRank = ranks.length ? Math.min(...ranks) : 1;
    const maxRank = ranks.length ? Math.max(...ranks) : 50;
    const avgRank = ranks.length
        ? Math.round(ranks.reduce((a, b) => a + b, 0) / ranks.length)
        : null;

    // Y축: 항상 1위부터, 하단 여유 추가
    const bottomPadding = Math.max(10, Math.ceil(maxRank * 0.08));
    const domain = [1, maxRank + bottomPadding];

    // 레이블 표시 인덱스: 첫점·끝점·최고·최저·오늘·4칸 간격
    const significantIndices = useMemo(() => {
        const set = new Set();
        if (!data.length) return set;
        const firstIdx = data.findIndex(d => d.rank);
        if (firstIdx >= 0) set.add(firstIdx);
        set.add(data.length - 1);
        const minIdx = data.findIndex(d => d.rank === minRank);
        const maxIdx = data.findIndex(d => d.rank === maxRank);
        if (minIdx >= 0) set.add(minIdx);
        if (maxIdx >= 0) set.add(maxIdx);
        const todayIdx = data.findIndex(d => d.isToday);
        if (todayIdx >= 0) set.add(todayIdx);
        for (let i = 0; i < data.length; i += 4) {
            if (data[i]?.rank) set.add(i);
        }
        return set;
    }, [data, minRank, maxRank]);

    // 라운드 박스 레이블 (첫점·끝점 가장자리 잘림 방지)
    const firstDataIdx = data.findIndex(d => d.rank);
    const renderLabel = ({ x, y, value, index }) => {
        if (!value || !significantIndices.has(index)) return null;
        const label = `${value}위`;
        const w = label.length * 7.5 + 10;
        const h = 17;
        const rectY = y - h - 7;

        // 마지막 점은 박스를 왼쪽으로, 첫 점은 오른쪽으로 밀어서 잘림 방지
        let rectX = x - w / 2;
        let textX = x;
        if (index === data.length - 1) {
            rectX = x - w;
            textX = x - w / 2;
        } else if (index === firstDataIdx) {
            rectX = x;
            textX = x + w / 2;
        }

        return (
            <g>
                <rect
                    x={rectX}
                    y={rectY}
                    width={w}
                    height={h}
                    rx={4}
                    ry={4}
                    fill="#4f46e5"
                    fillOpacity={0.88}
                />
                <text
                    x={textX}
                    y={rectY + h * 0.68}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={700}
                    fill="white"
                >
                    {label}
                </text>
            </g>
        );
    };

    // 추이
    const lastTwo = valid.slice(-2);
    let trend = null;
    if (lastTwo.length === 2) {
        const diff = lastTwo[1].rank - lastTwo[0].rank;
        if (diff < 0)
            trend = (
                <span style={{ color: '#ef4444', fontSize: 14, fontWeight: 700 }}>
                    ▲{Math.abs(diff)} 상승
                </span>
            );
        else if (diff > 0)
            trend = (
                <span style={{ color: '#3b82f6', fontSize: 14, fontWeight: 700 }}>
                    ▼{diff} 하락
                </span>
            );
        else
            trend = <span style={{ color: '#64748b', fontSize: 14, fontWeight: 700 }}>— 유지</span>;
    }

    return (
        <div className="full-chart-card">
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                }}
            >
                <div className="card-sub-title" style={{ fontSize: 14 }}>
                    <LineChartOutlined style={{ color: '#4f46e5' }} /> 내 상품 순위 추이
                    <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>
                        (최근 30일)
                    </span>
                    {!hasData && (
                        <Tag color="orange" style={{ fontSize: 11, verticalAlign: 'middle' }}>
                            전체 샘플
                        </Tag>
                    )}
                </div>
                {trend}
            </div>

            {hasData && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <StatChip label="최고" value={`${minRank}위`} color="#10b981" />
                    <StatChip label="최저" value={`${maxRank}위`} color="#ef4444" />
                    {avgRank && <StatChip label="평균" value={`${avgRank}위`} color="#f59e0b" />}
                    <StatChip label="데이터" value={`${valid.length}일`} />
                </div>
            )}

            <ResponsiveContainer width="100%" height={340}>
                <AreaChart data={data} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
                    <defs>
                        {/* 선 아래쪽(하위 순위 방향)으로 채우는 그라디언트 */}
                        <linearGradient id="rankGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#4f46e5" stopOpacity={0} />
                            <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.22} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        axisLine={false}
                        tickLine={false}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        reversed={true}
                        domain={domain}
                        tick={{ fontSize: 11, fill: '#94a3b8' }}
                        tickFormatter={v => `${v}위`}
                        width={42}
                        axisLine={false}
                        tickLine={false}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {avgRank && (
                        <ReferenceLine
                            y={avgRank}
                            stroke="#f59e0b"
                            strokeDasharray="5 4"
                            strokeWidth={1.5}
                            label={{
                                value: `평균 ${avgRank}위`,
                                fill: '#f59e0b',
                                fontSize: 11,
                                position: 'insideBottomRight',
                                dy: 22,
                            }}
                        />
                    )}
                    <Area
                        type="monotone"
                        dataKey="rank"
                        stroke="#4f46e5"
                        strokeWidth={2.5}
                        fill="url(#rankGradient)"
                        baseValue={domain[1]}
                        connectNulls={true}
                        dot={<CustomDot />}
                        activeDot={{ r: 5, fill: '#4f46e5', stroke: 'white', strokeWidth: 2 }}
                    >
                        <LabelList dataKey="rank" content={renderLabel} />
                    </Area>
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export default RankTrendChart;
